import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, History, Download } from "lucide-react";
import api from "../lib/api";
import { CommandCard } from "../components/CommandCard";

const Shell = ({ icon: Icon, title, subtitle, children }) => (
  <div className="min-h-screen bg-[#FAFAF7] py-10 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><Icon size={19} /></div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
      </div>
      <p className="text-sm text-slate-500 mb-8 ml-13">{subtitle}</p>
      {children}
    </div>
  </div>
);

export function Favourites() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/favourites").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <Shell icon={Heart} title="My Favourites" subtitle="Your saved commands, ready to copy.">
      {items && items.length === 0 && (
        <div className="text-center py-20" data-testid="favourites-empty">
          <p className="text-slate-500">No favourites yet. Tap the heart on any command to save it here.</p>
          <Link to="/app" className="inline-block mt-4 text-emerald-700 font-semibold hover:underline">Browse commands</Link>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="favourites-grid">
        {(items || []).map((c) => (
          <CommandCard key={c.id} cmd={c} onToggleFavourite={(slug, fav) => { if (!fav) setItems(items.filter((i) => i.slug !== slug)); }} />
        ))}
      </div>
    </Shell>
  );
}

export function Recent() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/recent").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  return (
    <Shell icon={History} title="Recently Used" subtitle="Commands you've viewed or copied recently.">
      {items && items.length === 0 && (
        <div className="text-center py-20" data-testid="recent-empty">
          <p className="text-slate-500">Nothing here yet. Commands you copy or view will appear here for quick reuse.</p>
          <Link to="/app" className="inline-block mt-4 text-emerald-700 font-semibold hover:underline">Browse commands</Link>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recent-grid">
        {(items || []).map((c) => <CommandCard key={c.id} cmd={c} />)}
      </div>
    </Shell>
  );
}

export function CheatSheet() {
  return (
    <Shell icon={Download} title="Quick-Command Cheat Sheet" subtitle="Your bonus download for Admin Professionals, Students, Managers, Small Businesses & Entrepreneurs.">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-lg" data-testid="cheatsheet-card">
        <p className="text-sm text-slate-600 leading-relaxed">One page of the most useful shorthand commands — print it, keep it next to your desk, or save it on your phone.</p>
        <a data-testid="cheatsheet-download-btn" href={`${process.env.REACT_APP_BACKEND_URL}/api/cheatsheet`}
          className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3.5 rounded-xl active:scale-[0.98] transition-all">
          <Download size={17} /> DOWNLOAD CHEAT SHEET
        </a>
      </div>

      <div className="mt-10 space-y-8" data-testid="cheatsheet-visual-previews">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Visual cheat sheets</h2>
          <p className="text-sm text-slate-500 mt-1">Save these to your phone, print them for your desk, or share them with your team.</p>
        </div>

        <figure className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <a href="/assets/cheatsheet-industry-20.png" target="_blank" rel="noreferrer" data-testid="cheatsheet-industry-image-link">
            <img src="/assets/cheatsheet-industry-20.png"
              alt="XpressPrompts — 20 short commands by industry (Banking, Insurance, Accounting, Events, Charities, Transport, Public Sector, Faith, Construction, Oil & Gas, Video Scripts, Healthcare, Education, Sports)"
              data-testid="cheatsheet-industry-image"
              className="w-full h-auto block hover:opacity-95 transition-opacity"
              loading="lazy" />
          </a>
          <figcaption className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-bold text-sm">20 Short Commands by Industry</p>
              <p className="text-xs text-slate-500">Banking, insurance, construction, healthcare, education + more.</p>
            </div>
            <a href="/assets/cheatsheet-industry-20.png" download="xpressprompts-20-industry-commands.png"
              data-testid="cheatsheet-industry-download"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-900 text-white px-3 py-2 rounded-lg hover:bg-slate-800">
              <Download size={13} /> Save image
            </a>
          </figcaption>
        </figure>

        <figure className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <a href="/assets/cheatsheet-personal-20.png" target="_blank" rel="noreferrer" data-testid="cheatsheet-personal-image-link">
            <img src="/assets/cheatsheet-personal-20.png"
              alt="XpressPrompts — 20 short commands for personal growth, career, money, productivity, communication, creativity, wellbeing"
              data-testid="cheatsheet-personal-image"
              className="w-full h-auto block hover:opacity-95 transition-opacity"
              loading="lazy" />
          </a>
          <figcaption className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-bold text-sm">You Can Do It Too — 20 Everyday Commands</p>
              <p className="text-xs text-slate-500">Confidence, career, money, productivity, learning, wellbeing.</p>
            </div>
            <a href="/assets/cheatsheet-personal-20.png" download="xpressprompts-20-everyday-commands.png"
              data-testid="cheatsheet-personal-download"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-900 text-white px-3 py-2 rounded-lg hover:bg-slate-800">
              <Download size={13} /> Save image
            </a>
          </figcaption>
        </figure>
      </div>
    </Shell>
  );
}
